/**
 * Simple semantic version comparison utility
 * Compares two version strings in the format "major.minor.patch"
 * @param {string} version1 - First version to compare
 * @param {string} version2 - Second version to compare
 * @returns {number} - Returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export function compareVersions(version1, version2) {
  if (!version1 || !version2) {
    return 0;
  }

  const v1Parts = version1.split(".").map(Number);
  const v2Parts = version2.split(".").map(Number);

  // Ensure both arrays have the same length by padding with zeros
  const maxLength = Math.max(v1Parts.length, v2Parts.length);
  while (v1Parts.length < maxLength) v1Parts.push(0);
  while (v2Parts.length < maxLength) v2Parts.push(0);

  for (let i = 0; i < maxLength; i++) {
    if (v1Parts[i] < v2Parts[i]) return -1;
    if (v1Parts[i] > v2Parts[i]) return 1;
  }

  return 0;
}

/**
 * Checks if the current app version is greater than the cached version
 * @param {string} currentVersion - Current app version
 * @param {string} cachedVersion - Version that created the cache
 * @returns {boolean} - True if current version is greater, false otherwise
 */
export function isVersionGreater(currentVersion, cachedVersion) {
  return compareVersions(currentVersion, cachedVersion) > 0;
}

/**
 * Checks if cache should be invalidated based on version comparison
 * @param {string} currentVersion - Current app version
 * @param {string} cachedVersion - Version that created the cache
 * @returns {boolean} - True if cache should be invalidated, false otherwise
 */
export function shouldInvalidateCache(currentVersion, cachedVersion) {
  if (!cachedVersion) {
    return true; // No cached version means cache should be invalidated
  }

  return isVersionGreater(currentVersion, cachedVersion);
}
